<template>
  <div class="card p-4 shadow-sm">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <h3 class="mb-0">Mesas del Restaurante</h3>
      <button class="btn btn-outline-primary btn-sm" @click="mostrarPlano = !mostrarPlano">
        {{ mostrarPlano ? "Ver tarjetas" : "Ver distribucion" }}
      </button>
    </div>

    <div v-if="!mostrarPlano" class="row g-3">
      <div
        v-for="mesa in mesasOrdenadas"
        :key="mesa.IdMesa"
        class="col-6 col-md-3"
      >
        <div
          class="mesa-card p-3 text-center rounded shadow-sm"
          :class="estadoClase(mesa.Estado)"
          @click="seleccionarMesa(mesa)"
        >
          <i class="bx bx-restaurant fs-1"></i>
          <h5 class="mt-2">Mesa {{ mesa.Numero }}</h5>
          <small>{{ estadoNombre(mesa.Estado) }}</small>
        </div>
      </div>
    </div>

    <div v-else class="plano-restaurante-wrapper">
      <div class="d-flex justify-content-center mb-3">
        <span class="badge bg-secondary">Distribucion del salon</span>
      </div>

      <div v-if="usarPlanoPersonalizado" class="plano-personalizado" :style="estiloPlanoPersonalizado">
        <div
          v-for="mesa in mesasConPosicion"
          :key="`pos-${mesa.IdMesa}`"
          class="mesa-plano rounded shadow-sm"
          :class="estadoClase(mesa.Estado)"
          :style="estiloMesaPlano(mesa)"
          @click="seleccionarMesa(mesa)"
        >
          <i class="bx bx-restaurant fs-4"></i>
          <div class="fw-bold mt-1">Mesa {{ mesa.Numero }}</div>
          <small>{{ estadoNombre(mesa.Estado) }}</small>
        </div>
      </div>

      <div v-else class="plano-restaurante-auto">
        <div
          v-for="mesa in mesasOrdenadas"
          :key="`auto-${mesa.IdMesa}`"
          class="mesa-plano rounded shadow-sm"
          :class="estadoClase(mesa.Estado)"
          @click="seleccionarMesa(mesa)"
        >
          <i class="bx bx-restaurant fs-4"></i>
          <div class="fw-bold mt-1">Mesa {{ mesa.Numero }}</div>
          <small>{{ estadoNombre(mesa.Estado) }}</small>
        </div>
      </div>

      <div v-if="usarPlanoPersonalizado && mesasSinPosicion.length" class="mesas-sin-posicion mt-4">
        <h6 class="mb-2">Mesas sin posicion fija</h6>
        <div class="plano-restaurante-auto">
          <div
            v-for="mesa in mesasSinPosicion"
            :key="`sin-pos-${mesa.IdMesa}`"
            class="mesa-plano rounded shadow-sm"
            :class="estadoClase(mesa.Estado)"
            @click="seleccionarMesa(mesa)"
          >
            <i class="bx bx-restaurant fs-4"></i>
            <div class="fw-bold mt-1">Mesa {{ mesa.Numero }}</div>
            <small>{{ estadoNombre(mesa.Estado) }}</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config";

export default {
  name: "MesasRestaurantePedido",
  data() {
    return {
      mesas: [],
      mostrarPlano: false,
    };
  },
  computed: {
    mesasOrdenadas() {
      return [...this.mesas].sort((a, b) => {
        const ordenA = this.normalizarNumeroPlano(a.OrdenPlano);
        const ordenB = this.normalizarNumeroPlano(b.OrdenPlano);

        if (ordenA !== null && ordenB !== null && ordenA !== ordenB) {
          return ordenA - ordenB;
        }
        if (ordenA !== null && ordenB === null) {
          return -1;
        }
        if (ordenA === null && ordenB !== null) {
          return 1;
        }

        const numeroA = Number(a.Numero);
        const numeroB = Number(b.Numero);

        if (!Number.isNaN(numeroA) && !Number.isNaN(numeroB)) {
          return numeroA - numeroB;
        }

        return String(a.Numero).localeCompare(String(b.Numero), "es", {
          numeric: true,
          sensitivity: "base",
        });
      });
    },
    mesasConPosicion() {
      return this.mesasOrdenadas.filter((mesa) => {
        return this.normalizarNumeroPlano(mesa.FilaPlano) !== null && this.normalizarNumeroPlano(mesa.ColumnaPlano) !== null;
      });
    },
    mesasSinPosicion() {
      return this.mesasOrdenadas.filter((mesa) => {
        return this.normalizarNumeroPlano(mesa.FilaPlano) === null || this.normalizarNumeroPlano(mesa.ColumnaPlano) === null;
      });
    },
    usarPlanoPersonalizado() {
      return this.mesasConPosicion.length > 0;
    },
    maxColumnasPlano() {
      const columnas = this.mesasConPosicion
        .map((mesa) => this.normalizarNumeroPlano(mesa.ColumnaPlano))
        .filter((valor) => valor !== null);

      if (!columnas.length) {
        return 4;
      }

      return Math.max(...columnas, 4);
    },
    estiloPlanoPersonalizado() {
      return {
        gridTemplateColumns: `repeat(${this.maxColumnasPlano}, minmax(120px, 1fr))`,
      };
    },
  },
  methods: {
    async obtenerMesas() {
      const { data } = await axios.get(`${API_URL}/mesas`);
      this.mesas = data;
    },
    estadoClase(estado) {
      switch (estado) {
        case 0:
          return "mesa-libre";
        case 1:
          return "mesa-ocupada";
        case 2:
          return "mesa-fuera";
        default:
          return "";
      }
    },
    estadoNombre(estado) {
      switch (estado) {
        case 0:
          return "Libre";
        case 1:
          return "Ocupada";
        case 2:
          return "Fuera de servicio";
        default:
          return "Desconocido";
      }
    },
    normalizarNumeroPlano(valor) {
      const numero = Number(valor);
      if (!Number.isInteger(numero) || numero < 1) {
        return null;
      }
      return numero;
    },
    estiloMesaPlano(mesa) {
      const fila = this.normalizarNumeroPlano(mesa.FilaPlano);
      const columna = this.normalizarNumeroPlano(mesa.ColumnaPlano);

      if (fila === null || columna === null) {
        return {};
      }

      return {
        gridRow: `${fila}`,
        gridColumn: `${columna}`,
      };
    },
    seleccionarMesa(mesa) {
      this.$router.push({ name: "MenuMesero", params: { idMesa: mesa.IdMesa } });
    },
  },
  mounted() {
    this.obtenerMesas();
  },
};
</script>

<style scoped>
.mesa-card {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}
.mesa-card:hover {
  transform: scale(1.05);
}

.plano-restaurante-wrapper {
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
}

.plano-personalizado,
.plano-restaurante-auto {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.mesa-plano {
  cursor: pointer;
  text-align: center;
  padding: 12px 10px;
  transition: transform 0.2s ease-in-out;
}

.mesa-plano:hover {
  transform: translateY(-2px);
}

.mesa-libre {
  background-color: #d4edda; /* Verde suave */
  color: #155724;
}
.mesa-ocupada {
  background-color: #fff3cd; /* Amarillo suave */
  color: #856404;
}
.mesa-fuera {
  background-color: #f8d7da; /* Rojo suave */
  color: #721c24;
}

.mesas-sin-posicion {
  border-top: 1px solid #d0d5dd;
  padding-top: 16px;
}

@media (max-width: 768px) {
  .plano-personalizado,
  .plano-restaurante-auto {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
}
</style>
